import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { getScrollY, scrollToY, getMaxScrollY } from '../utils/scrolling';

export type Route = 'home' | 'login' | 'logout' | 'card_detail' | 'tag' | 'color' | 'about' | '404' | '403';

interface RouterProps {
  children: (route: Route, params: Record<string, string>) => React.ReactNode;
}

export function Router({ children }: RouterProps) {
  const [route, setRoute] = useState<Route>('home');
  const [params, setParams] = useState<Record<string, string>>({});
  // 戻る・進むで復元すべきスクロール状態。ルート描画のコミット後に消費する
  const pendingScrollRef = useRef<SavedScrollState | null>(null);

  const parseUrl = () => {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const paramsObj: Record<string, string> = {};
    
    // URLパラメータを解析
    searchParams.forEach((value, key) => {
      paramsObj[key] = value;
    });

    if (path === '/about') {
      setRoute('about');
      setParams(paramsObj);
    } else if (path === '/px/admin') {
      setRoute('login');
      setParams(paramsObj);
    } else if (path === '/px/logout') {
      setRoute('logout');
      setParams(paramsObj);
    } else if (/^\/\d{4}\/\d{2}$/.test(path)) {
      // /2025/08 形式の月別パーマリンク
      const [, year, month] = path.split('/');
      setRoute('home');
      setParams({ ...paramsObj, month: `${year}-${month}` });
    } else if (path === '/colors') {
      setRoute('color');
      setParams({ ...paramsObj, colorName: '' });
    } else if (path.startsWith('/colors/')) {
      setRoute('color');
      const colorName = decodeURIComponent(path.split('/colors/')[1]);
      setParams({ ...paramsObj, colorName });
    } else if (path === '/tags') {
      setRoute('tag');
      setParams({ ...paramsObj, tagName: '' });
    } else if (path.startsWith('/tags/')) {
      setRoute('tag');
      const tagName = decodeURIComponent(path.split('/tags/')[1]);
      setParams({ ...paramsObj, tagName });
    } else if (path.startsWith('/c/')) {
      setRoute('card_detail');
      const cardId = path.split('/c/')[1];
      setParams({ ...paramsObj, cardId });
    } else if (path === '/') {
      setRoute('home');
      setParams(paramsObj);
    } else {
      setRoute('404');
      setParams(paramsObj);
    }
  };

  useEffect(() => {
    // ブラウザ標準のスクロール自動復元を無効化。
    // カードは非同期で読み込まれるため、読み込み完了後にブラウザが前回位置へ
    // ジャンプさせ、ページ先頭のヘッダーが隠れて見える問題を防ぐ
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    parseUrl();

    const handlePopState = () => {
      // 復元すべきスクロール状態を控えてからルートを更新する。
      // 実際の復元は、戻り先のページがReactに描画された後（下のuseLayoutEffect）で行う
      const state = window.history.state;
      pendingScrollRef.current =
        state && typeof state.scrollY === 'number' ? state : { scrollY: 0 };
      parseUrl();
    };

    const handleNavigate = () => {
      // 新しいページはトップから表示。実際のスクロールは描画確定後（useLayoutEffect）に
      // 行うため、遷移前の画面が一瞬動いて見えることはない
      pendingScrollRef.current = { scrollY: 0 };
      parseUrl();
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('navigate', handleNavigate);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('navigate', handleNavigate);
    };
  }, []);

  // 戻る・進むによるルート描画のコミット直後（ペイント前）にスクロール位置を復元する。
  // この時点で戻り先ページのDOMは確定しているので、アンカー要素を正確に測れる
  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    pendingScrollRef.current = null;
    restoreScroll(pending);
  }, [route, params]);

  return <>{children(route, params)}</>;
}

// スクロール位置を復元する。
// モバイルではアドレスバーの伸縮でビューポート高さが変わり、scrollYのピクセル値では
// ずれるため、「画面上部に見えていたカード」を同じ画面位置に戻すアンカー方式を優先する。
// アンカー要素がまだ描画されていなければ（非同期読み込み中）描画を待ち、
// 期限までに現れなければscrollYにフォールバックする。
function restoreScroll(state: SavedScrollState, deadline = Date.now() + 3000) {
  if (state.anchor) {
    const el = document.querySelector(`a[href="${state.anchor.href}"]`);
    if (el) {
      const top = el.getBoundingClientRect().top;
      const target = getScrollY() + top - state.anchor.top;
      // html { scroll-behavior: smooth } の影響を受けないよう即時スクロールで復元
      scrollToY(target);
      return;
    }
    if (Date.now() <= deadline) {
      requestAnimationFrame(() => restoreScroll(state, deadline));
      return;
    }
  }
  // ピクセル値フォールバック。復元先の高さが確保されるまで待つ（クランプ防止）
  const maxScroll = getMaxScrollY();
  if (maxScroll >= state.scrollY || Date.now() > deadline) {
    scrollToY(state.scrollY);
  } else {
    requestAnimationFrame(() => restoreScroll(state, deadline));
  }
}

interface SavedScrollState {
  scrollY: number;
  // 画面上部に見えていたカードのリンクと、その画面上の位置
  anchor?: { href: string; top: number };
}

export function navigate(path: string) {
  // 現在のスクロール位置と、画面上部に見えているカードを保存（戻ってきた時に復元するため）
  const state: SavedScrollState = { scrollY: getScrollY() };
  const cardLinks = document.querySelectorAll('a[href^="/c/"]');
  for (const el of Array.from(cardLinks)) {
    const rect = el.getBoundingClientRect();
    if (rect.bottom > 0) {
      const href = el.getAttribute('href');
      if (href) state.anchor = { href, top: rect.top };
      break;
    }
  }
  window.history.replaceState(state, '', window.location.pathname);

  // 新しいページに遷移（スクロール位置は保存しない）
  window.history.pushState({ scrollY: 0 }, '', path);

  // popstateイベントの代わりに、カスタムイベントを発火。
  // 新しいページのスクロール位置はRouterが描画確定後にトップへ合わせる
  window.dispatchEvent(new CustomEvent('navigate', { detail: { path } }));
}

export function useRouter() {
  return { navigate };
}
